/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from 'react';
import { motion } from 'motion/react';
import { 
  ArrowLeft, 
  CheckCircle2, 
  AlertTriangle, 
  FileText, 
  Download, 
  Printer, 
  Clock, 
  History,
  Eye,
  Beaker,
  FlaskConical,
  User,
  ShieldCheck,
  Search
} from 'lucide-react';

interface SterilityObservationDetailsProps {
  onBack: () => void;
  onViewTest: () => void;
}

const SterilityObservationDetails: React.FC<SterilityObservationDetailsProps> = ({ onBack, onViewTest }) => {
  const testData = {
    testId: 'ST-2024-X45',
    productName: 'Insulin Glargine',
    productBatch: 'B240501',
    testMethod: 'Membrane Filtration',
    mediaType: 'FTM (Fluid Thioglycollate Medium)',
    tubeId: 'T-FTM-01',
    observationDay: 'Day 3',
    status: 'Completed'
  };

  const results = {
    clarity: 'Clear',
    indicators: {
      colorChange: 'No',
      gasFormation: 'No',
      visibleParticles: 'No',
      surfaceFilm: 'No',
      sedimentFormation: 'No'
    },
    remarks: 'Routine inspection completed. No turbidity or colony formation observed after 72 hours of incubation.',
    result: 'PASS',
    recordedBy: 'P Shyam Kumar (TECH-8821)',
    recordedAt: '15-Aug-2024 09:30 AM'
  };

  const timeline = [
    { day: 'Day 3', status: 'Completed', date: '15-Aug-2024', result: 'PASS' },
    { day: 'Day 7', status: 'Pending', date: '19-Aug-2024', result: '-' },
    { day: 'Day 14', status: 'Pending', date: '26-Aug-2024', result: '-' }
  ];

  const auditTrail = [
    { action: 'Observation Recorded', user: 'P Shyam Kumar', timestamp: '15-Aug-2024 09:30:12', oldVal: 'Pending', newVal: 'Completed' },
    { action: 'System Validation', user: 'System-Auto', timestamp: '15-Aug-2024 09:30:15', oldVal: '-', newVal: 'Pass' },
    { action: 'QA Review', user: 'Harish S', timestamp: '15-Aug-2024 10:45:00', oldVal: 'Unverified', newVal: 'Verified' }
  ];

  return (
    <div className="min-h-screen bg-slate-50/30 flex flex-col pt-4 media-prep-scope">
      {/* Header Sticky Area */}
      <header className="bg-white border-b border-slate-200 px-8 py-6 sticky top-0 z-30 shadow-sm">
        <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <button 
              onClick={onBack}
              className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0F172A] hover:bg-slate-100 transition-all shadow-sm"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-black text-[#0F172A] tracking-tight">{testData.testId}</h1>
                <span className="px-3 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border bg-emerald-50 text-emerald-600 border-emerald-100">
                  {testData.observationDay} Recorded
                </span>
                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 text-slate-500 text-[9px] font-black rounded uppercase tracking-widest border border-slate-200">
                   <ShieldCheck className="w-3 h-3" />
                   READ-ONLY RECORD
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-slate-500 font-bold text-[11px] uppercase">
                <div className="flex items-center gap-1.5"><span className="text-slate-400">PRODUCT:</span> {testData.productName} ({testData.productBatch})</div>
                <div className="flex items-center gap-1.5"><span className="text-slate-400">METHOD:</span> {testData.testMethod}</div>
                <div className="flex items-center gap-1.5"><span className="text-slate-400">TUBE:</span> {testData.tubeId}</div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 font-black rounded-xl hover:bg-slate-50 transition-all text-[10px] uppercase tracking-widest shadow-sm">
              <Download className="w-4 h-4" />
              Download PDF
            </button>
            <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 font-black rounded-xl hover:bg-slate-50 transition-all text-[10px] uppercase tracking-widest shadow-sm">
              <Printer className="w-4 h-4" />
              Print Report
            </button>
            <button 
              onClick={onViewTest}
              className="flex items-center gap-2 px-6 py-2.5 bg-[#0F172A] text-white font-black rounded-xl shadow-lg shadow-slate-900/10 hover:scale-105 transition-all text-[10px] uppercase tracking-widest"
            >
              <FileText className="w-4 h-4" />
              View Test Details
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1600px] mx-auto w-full p-8 grid grid-cols-1 xl:grid-cols-12 gap-8 pb-32 focus-mode-main">
        {/* Left Section - Timeline & Audit summary */}
        <div className="xl:col-span-3 space-y-6">
           <div className="bg-white rounded-[2rem] border border-slate-200/60 p-6 shadow-sm">
              <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest mb-8 flex items-center gap-2">
                <History className="w-4 h-4 text-blue-500" />
                Observation Timeline
              </h3>
              <div className="space-y-8 relative before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-50">
                 {timeline.map((item, idx) => (
                   <div key={idx} className="relative pl-10">
                      <div className={`absolute left-0 top-1.5 w-8 h-8 rounded-full border-4 border-white shadow-md flex items-center justify-center z-10 
                        ${item.status === 'Completed' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                         {item.status === 'Completed' ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" /> }
                      </div>
                      <div className="space-y-1">
                         <p className={`text-[11px] font-black uppercase tracking-widest ${item.status === 'Completed' ? 'text-slate-800' : 'text-slate-400'}`}>
                            {item.day}
                         </p>
                         <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-slate-400">{item.date}</span>
                            {item.result === 'PASS' && <span className="text-[9px] font-black text-emerald-500 uppercase">PASS</span>}
                         </div>
                      </div>
                   </div>
                 ))}
              </div>
           </div>

           <div className="bg-[#0F172A] rounded-[2rem] p-6 text-white shadow-xl">
              <h3 className="text-[10px] font-black uppercase tracking-widest mb-6 text-slate-400 border-b border-white/10 pb-3">Recorded Information</h3>
              <div className="space-y-6">
                 <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center text-white/60">
                       <User className="w-4 h-4" />
                    </div>
                    <div>
                       <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Operator</p>
                       <p className="text-xs font-bold text-white">{results.recordedBy}</p>
                    </div>
                 </div>
                 <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center text-white/60">
                       <Clock className="w-4 h-4" />
                    </div>
                    <div>
                       <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Recorded On</p>
                       <p className="text-xs font-bold text-white">{results.recordedAt}</p>
                    </div>
                 </div>
              </div>
           </div>
        </div>

        {/* Center Section - Results Details */}
        <div className="xl:col-span-6 space-y-6">
           <div className="bg-white rounded-[2.5rem] border border-slate-200/60 p-8 shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-50 pb-6 mb-8">
                <div className="flex items-center gap-3">
                   <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                      <Eye className="w-6 h-6" />
                   </div>
                   <div>
                      <h3 className="text-sm font-black text-[#0F172A] tracking-tight">VISUAL CLARITY ASSESSMENT</h3>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">GMP Read-Only Record</p>
                   </div>
                </div>
                <div className="flex flex-col items-end">
                   <span className="text-[9px] font-black text-slate-400 uppercase mb-1">Assessment Value</span>
                   <span className="px-4 py-2 bg-[#0F172A] text-white rounded-xl text-xs font-black uppercase tracking-wider">{results.clarity}</span>
                </div>
              </div>

              <div className="space-y-6">
                 <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Contamination Indicators</h4>
                 <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(results.indicators).map(([key, val]) => (
                      <div key={key} className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                         <p className="text-[9px] font-black text-slate-400 uppercase mb-1.5">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                         </p>
                         <div className={`text-xs font-black ${val === 'Yes' ? 'text-rose-500' : 'text-emerald-600'}`}>
                            {val}
                         </div>
                      </div>
                    ))}
                 </div>
              </div>

              <div className="mt-8 pt-8 border-t border-slate-50">
                 <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Technician Remarks</h4>
                 <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 min-h-[120px]">
                    <p className="text-sm font-medium text-slate-700 leading-relaxed italic">
                      "{results.remarks}"
                    </p>
                 </div>
              </div>
           </div>

           <div className="bg-white rounded-[2.5rem] border border-slate-200/60 p-8 shadow-sm">
               <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest mb-6 flex items-center gap-2">
                 <AlertTriangle className="w-4 h-4 text-purple-500" />
                 Audit Trail & Traceability
               </h3>
               <div className="overflow-hidden border border-slate-100 rounded-3xl">
                  <table className="w-full text-left">
                     <thead className="bg-slate-50">
                        <tr className="text-[12px] font-black text-slate-400 uppercase tracking-widest">
                           <th className="px-4 py-2">Timestamp</th>
                           <th className="px-4 py-3">Action</th>
                           <th className="px-4 py-2">User</th>
                           <th className="px-4 py-2">Changes</th>
                        </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-50">
                        {auditTrail.map((log, idx) => (
                          <tr key={idx} className="text-[12px] group hover:bg-slate-50 transition-colors">
                             <td className="px-4 py-2 font-bold text-slate-500">{log.timestamp}</td>
                             <td className="px-4 py-2 font-black text-slate-800">{log.action}</td>
                             <td className="px-4 py-2 font-bold text-blue-600">{log.user}</td>
                             <td className="px-4 py-2">
                                <div className="flex items-center gap-1">
                                   <span className="text-slate-400 line-through">{log.oldVal === '-' ? 'N/A' : log.oldVal}</span>
                                   <span className="text-slate-300">→</span>
                                   <span className="font-black text-[#0F172A]">{log.newVal}</span>
                                </div>
                             </td>
                          </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
           </div>
        </div>

        {/* Right Section - Result summary & Attachments */}
        <div className="xl:col-span-3 space-y-6">
           <div className="bg-white rounded-[2rem] border border-slate-200/60 p-8 shadow-xl shadow-slate-200/20 sticky top-32">
              <div className="flex flex-col items-center justify-center py-10 bg-emerald-50/50 rounded-[2.5rem] border border-emerald-100 mb-8 border-dashed">
                 <p className="text-[10px] font-black text-emerald-600/50 uppercase tracking-widest mb-4">Verified Result</p>
                 <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center text-white shadow-lg shadow-emerald-500/20 mb-4 scale-110">
                    <CheckCircle2 className="w-12 h-12" />
                 </div>
                 <span className="text-3xl font-black text-emerald-600 tracking-widest">PASS</span>
              </div>

              <div className="space-y-6">
                 <div>
                    <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Attachments (1)</h4>
                    <div className="space-y-2">
                       <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between group cursor-pointer hover:bg-white hover:border-blue-200 transition-all">
                          <div className="flex items-center gap-3">
                             <Eye className="w-4 h-4 text-blue-500" />
                             <div>
                                <p className="text-[11px] font-black text-slate-700">OBS_PHOTO_D3_001.JPG</p>
                                <p className="text-[9px] font-bold text-slate-400 uppercase">2.4 MB • 15:30:12</p>
                             </div>
                          </div>
                       </div>
                    </div>
                 </div>

                 <div className="p-4 bg-blue-50/30 rounded-2xl border border-blue-100 border-dashed">
                    <div className="flex gap-3 items-start mb-3">
                       <ShieldCheck className="w-4 h-4 text-blue-500 shrink-0" />
                       <span className="text-[10px] font-black text-blue-900 uppercase">GMP Certification</span>
                    </div>
                    <p className="text-[10px] font-bold text-blue-700 leading-tight">
                      This observation record is electronically signed and compliant with 21 CFR Part 11 requirements.
                    </p>
                 </div>
              </div>
           </div>
        </div>
      </main>
    </div>
  );
};

export default SterilityObservationDetails;
